// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabase'

const AuthContext = createContext(null)

const PERMISSIONS = {
  super_admin: {
    documents:    { read: true,  write: true,  delete: true  },
    clients:      { read: true,  write: true,  delete: true  },
    fournisseurs: { read: true,  write: true,  delete: true  },
    catalogue:    { read: true,  write: true,  delete: true  },
    bc:           { read: true,  write: true,  delete: true  },
    settings:     { read: true,  write: true                 },
    users:        { read: true,  write: true,  delete: true  },
    import:       { read: true,  write: true                 },
    catalogue_ia: { write: true                              },
  },
  gestionnaire: {
    documents:    { read: true,  write: true,  delete: true  },
    clients:      { read: true,  write: true,  delete: true  },
    fournisseurs: { read: true,  write: true,  delete: true  },
    catalogue:    { read: true,  write: true,  delete: true  },
    bc:           { read: true,  write: true,  delete: true  },
    settings:     { read: true,  write: true                 },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
    catalogue_ia: { write: true                              },
  },
  commercial: {
    documents:    { read: true,  write: true,  delete: false },
    clients:      { read: true,  write: true,  delete: false },
    fournisseurs: { read: true,  write: true,  delete: false },
    catalogue:    { read: true,  write: true,  delete: false },
    bc:           { read: true,  write: true,  delete: false },
    settings:     { read: true,  write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
    catalogue_ia: { write: false                             },
  },
  comptable: {
    documents:    { read: true,  write: false, delete: false },
    clients:      { read: true,  write: false, delete: false },
    fournisseurs: { read: true,  write: false, delete: false },
    catalogue:    { read: true,  write: false, delete: false },
    bc:           { read: true,  write: false, delete: false },
    settings:     { read: true,  write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
    catalogue_ia: { write: false                             },
  },
  lecture: {
    documents:    { read: true,  write: false, delete: false },
    clients:      { read: true,  write: false, delete: false },
    fournisseurs: { read: true,  write: false, delete: false },
    catalogue:    { read: true,  write: false, delete: false },
    bc:           { read: true,  write: false, delete: false },
    settings:     { read: false, write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
    catalogue_ia: { write: false                             },
  },
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(undefined) // undefined = pas encore initialisé
  const [profil,  setProfil]  = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    // Initialisation séquentielle — une seule opération Auth à la fois
    async function init() {
      try {
        // 1. Récupérer la session — timeout 5s pour éviter la boucle infinie si réseau lent
        const fallback = new Promise(resolve =>
          setTimeout(() => resolve({ data: { session: null } }), 5000)
        )
        const { data: { session } } = await Promise.race([
          supabase.auth.getSession(),
          fallback,
        ])
        if (!active) return

        const u = session?.user ?? null
        setUser(u)

        // 2. Si connecté, charger le profil
        if (u) {
          const { data } = await supabase
            .from('profils')
            .select('*')
            .eq('id', u.id)
            .single()
          if (active && data) setProfil(data)
        }
      } catch (err) {
        console.warn('Auth init error:', err.message)
      } finally {
        if (active) setLoading(false)
      }
    }

    init()

    // Écouter les changements de session APRÈS l'init
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!active) return
        const u = session?.user ?? null
        setUser(u)

        if (u) {
          const { data } = await supabase
            .from('profils')
            .select('*')
            .eq('id', u.id)
            .single()
          if (active && data) setProfil(data)
        } else {
          setProfil(null)
        }
        setLoading(false)
      }
    )

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [])

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  const logout = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfil(null)
  }

  const updateMyProfil = async (updates) => {
    const { data, error } = await supabase
      .from('profils').update(updates).eq('id', user.id).select().single()
    if (error) throw error
    setProfil(data)
    return data
  }

  const can = (resource, action) => {
    if (!profil) return false
    const custom = profil.permissions_custom?.[resource]?.[action]
    if (custom !== undefined) return custom
    return PERMISSIONS[profil.role]?.[resource]?.[action] ?? false
  }

  const isAdmin   = ()     => profil?.role === 'super_admin'
  const isAtLeast = (role) => {
    const h = ['lecture', 'comptable', 'commercial', 'gestionnaire', 'super_admin']
    return h.indexOf(profil?.role) >= h.indexOf(role)
  }

  // Afficher l'écran de chargement tant que l'auth n'est pas initialisée
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Bricolage Grotesque', sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '2.5rem', fontWeight: 800,
            background: 'linear-gradient(135deg, #FF6B35, #00D4AA)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            marginBottom: '1rem',
          }}>
            ☀️ SOLSTICE
          </div>
          <div style={{ color: '#64748B', fontSize: '0.9rem' }}>
            Connexion en cours…
          </div>
          {/* Barre de progression animée */}
          <div style={{
            width: 200, height: 3, background: 'rgba(255,255,255,0.1)',
            borderRadius: 99, margin: '1rem auto 0', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: '40%', borderRadius: 99,
              background: 'linear-gradient(90deg, #FF6B35, #00D4AA)',
              animation: 'slide 1.2s ease-in-out infinite',
            }} />
          </div>
          <style>{`
            @keyframes slide {
              0%   { transform: translateX(-100%) }
              100% { transform: translateX(600%) }
            }
          `}</style>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={{
      user, profil, loading,
      login, logout, updateMyProfil,
      can, isAdmin, isAtLeast,
      role: profil?.role,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>')
  return ctx
}

export default AuthContext
